import { useState, useEffect, useMemo } from 'react';
import {
    useCreateDealerInvoiceMutation,
    useUpdateDealerInvoiceMutation,
    useSaveDealerInvoiceDraftMutation,
    useUpdateDealerInvoiceDraftMutation,
    useSubmitDealerInvoiceDraftMutation,
    useUploadInvoiceDocumentMutation,
    useDeleteInvoiceDocumentMutation,
    useGetApprovalDetailQuery,
    useApproveDealerInvoiceMutation,
    useRejectDealerInvoiceMutation,
    useReturnDealerInvoiceMutation,
    useLazyGetInvoiceDocumentByIdQuery
} from '../../../../../api/endpoints/dealerInvoiceEntryApi';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../../api/endpoints/documentContent';
import DocumentDownloadService from '../../../../../utils/documentDownloadService';
import { InvoiceStatus } from '../constants/invoiceStatus';

// Parametreye isWarrantyTeam eklendi
export const useDealerInvoiceOperations = (refetchList, th, isDomesticDealer, isWarrantyTeam) => {
    const [toastRef, setToastRef] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [formData, setFormData] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [sendAttempted, setSendAttempted] = useState(false);
    const [newFiles, setNewFiles] = useState([]);
    const [existingDocuments, setExistingDocuments] = useState([]);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // API Hooks
    const [createInvoice, { isLoading: isCreating }] = useCreateDealerInvoiceMutation();
    const [updateInvoice, { isLoading: isUpdating }] = useUpdateDealerInvoiceMutation();
    const [saveDraft] = useSaveDealerInvoiceDraftMutation();
    const [updateDraft] = useUpdateDealerInvoiceDraftMutation();
    const [submitDraft] = useSubmitDealerInvoiceDraftMutation();
    const [uploadDocument, { isLoading: isUploading }] = useUploadInvoiceDocumentMutation();
    const [deleteDocument] = useDeleteInvoiceDocumentMutation();

    // DOKÜMAN LİSTESİ ÇEKME (BAYİ İÇİN) - History dialog'daki mantığın aynısı
    const [triggerGetDocuments, { isFetching: isLoadingDocuments }] = useLazyGetInvoiceDocumentByIdQuery();

    const [triggerDownloadByKey] = useLazyDownloadDocumentByKeyQuery();

    // Garanti Operasyonları
    const [approveInvoice, { isLoading: isApproving }] = useApproveDealerInvoiceMutation();
    const [rejectInvoice, { isLoading: isRejecting }] = useRejectDealerInvoiceMutation();
    const [returnInvoice, { isLoading: isReturning }] = useReturnDealerInvoiceMutation();

    // GARANTİ DETAY SORGUSU (Sadece Garanti Ekibi ise çalışsın)
    const { data: detailData, isFetching: isLoadingApprovalDetail } = useGetApprovalDetailQuery(
        selectedRecord?.groupId || selectedRecord?.vbeln,
        {
            // Eğer Edit modu değilse VEYA Garanti ekibi değilse bu sorguyu ATLAMA (SKIP)
            skip: !isEditMode || (!selectedRecord?.groupId && !selectedRecord?.vbeln) || !isWarrantyTeam
        }
    );

    const isLoadingDetail = isLoadingApprovalDetail || isLoadingDocuments;
    const downloadService = useMemo(() => new DocumentDownloadService(toastRef), [toastRef]);

    const getRecordId = (row) => row?.recordId || row?.RecordId || row?.id || row?.Id;

    const isDraftEdit = isEditMode && formData.status === InvoiceStatus.DRAFT;

    // --- FORM DOLDURMA MANTIĞI ---
    useEffect(() => {
        if (!isEditMode) return;

        // SENARYO 1: Garanti Ekibi (Detay servisinden gelen veri)
        if (isWarrantyTeam && detailData) {
            const data = detailData.items ? detailData.items[0] : detailData;
            setFormData({
                id: data.id,
                groupId: data.groupId,
                invoiceNumber: data.invoiceNumber,
                invoiceDate: data.invoiceDate ? new Date(data.invoiceDate) : null,
                description: data.description,
                totalPrice: data.totalPrice || data.totalAmount,
                currency: data.currency,
                status: data.status,
                returnReason: data.returnReason,
                rejectionReason: data.rejectionReason
            });

            // Garanti ekibi için dokümanlar detay servisinden gelir
            if (data.documents) {
                setExistingDocuments(data.documents.map(doc => ({
                    id: doc.id,
                    fileName: doc.fileName,
                    fileKey: doc.fileKey || doc.id,
                    url: doc.url
                })));
            }
        }
        // SENARYO 2: Bayi (Tablodan gelen satır verisi + Doküman fetch)
        else if (!isWarrantyTeam && selectedRecord) {
            setFormData({
                id: getRecordId(selectedRecord),
                groupId: selectedRecord.groupId || selectedRecord.vbeln,
                invoiceNumber: selectedRecord.invoiceNumber,
                invoiceDate: selectedRecord.invoiceDate ? new Date(selectedRecord.invoiceDate) : null,
                description: selectedRecord.description,
                totalPrice: selectedRecord.totalAmount || selectedRecord.totalPrice,
                currency: selectedRecord.currency || 'TRY',
                status: selectedRecord.status,
                returnReason: selectedRecord.returnReason,
                rejectionReason: selectedRecord.rejectionReason
            });
        }
    }, [detailData, isEditMode, isWarrantyTeam, selectedRecord]);

    // --- BAYİ DOKÜMANLARI GELDİĞİNDE STATE GÜNCELLEME ---
    const mapDocuments = (documentsData) => {
        // API yapısına göre data direkt array veya data.data içinde olabilir
        const docs = Array.isArray(documentsData) ? documentsData : (documentsData?.data || []);

        return docs.map(doc => ({
            id: doc.id,
            fileName: doc.fileName,
            fileKey: doc.fileKey || doc.id, // İndirme için gerekli key
            url: doc.url
        }));
    };

    const loadExistingDocuments = async (groupId) => {
        if (!groupId) return;
        try {
            const documentsData = await triggerGetDocuments(groupId).unwrap();
            setExistingDocuments(mapDocuments(documentsData));
        } catch (error) {
            console.error("Documents Error:", error);
        }
    };

    // --- OPEN ACTIONS ---
    const openNew = (rowData) => {
        // ... (burası aynı kalacak)
        setFormData({
            groupId: rowData.vbeln || rowData.groupId,
            totalPrice: rowData.total_price || rowData.totalPrice || rowData.totalAmount,
            currency: rowData.currency || 'TRY',
            invoiceNumber: '',
            invoiceDate: null,
            description: ''
        });
        setSelectedRecord(rowData);
        setNewFiles([]);
        setExistingDocuments([]);
        setIsEditMode(false);
        setSubmitted(false);
        setSendAttempted(false);
        setDialogVisible(true);
    };

    const openEdit = (rowData) => {
        setSelectedRecord(rowData);
        setNewFiles([]);
        setExistingDocuments([]); // Önce temizle
        setIsEditMode(true);
        setSubmitted(false);
        setSendAttempted(false);
        setDialogVisible(true);

        // EĞER BAYİ İSE DOKÜMANLARI ÇEKMEYİ TETİKLE
        if (!isWarrantyTeam) {
            const groupId = rowData.groupId || rowData.vbeln;
            if (groupId) {
                loadExistingDocuments(groupId); // <-- API isteğini atıyoruz
            }
        }
    };

    const hideDialog = () => {
        setDialogVisible(false);
        setSubmitted(false);
        setSendAttempted(false);
        setFormData({});
        setSelectedRecord(null);
    };

    // ... (onInputChange, onDateChange, onTextareaChange, onFilesChange, setToastCallback, showToast, vb. fonksiyonlar aynen kalacak) ...
    const setToastCallback = (toast) => { setToastRef(toast); };
    const showToast = (severity, summary, detail) => { if (toastRef?.current) { toastRef.current.show({ severity, summary, detail, life: 3000 }); } };
    const onInputChange = (e, name) => setFormData(prev => ({ ...prev, [name]: (e.target && e.target.value) || '' }));
    const onDateChange = (e, name) => setFormData(prev => ({ ...prev, [name]: e.value }));
    const onTextareaChange = (e, name) => setFormData(prev => ({ ...prev, [name]: e.target.value || '' }));
    const onFilesChange = (files) => setNewFiles(files);

    const getErrorDetail = (error, fallbackKey) => error?.data?.errorMessage || th(fallbackKey);

    const validateFields = () => {
        setSubmitted(true);

        if (!formData.invoiceNumber || !formData.invoiceDate || !formData.description || !formData.description.trim()) {
            showToast('warn', th('common.warning'), th('dealerInvoice.messages.validationRequired'));
            return false;
        }

        return true;
    };

    const hasAnyDocument = () =>
        (existingDocuments && existingDocuments.length > 0) || (newFiles && newFiles.length > 0);

    const validateForSend = () => {
        setSendAttempted(true);

        if (!validateFields()) return false;

        if (!hasAnyDocument()) {
            showToast('error', th('common.error'), th('dealerInvoice.messages.fileRequired'));
            return false;
        }

        return true;
    };

    const buildDraftPayload = () => ({
        RecordId: formData.id,
        InvoiceNumber: formData.invoiceNumber,
        InvoiceDate: formData.invoiceDate,
        Description: formData.description.trim()
    });

    const uploadFiles = async (groupId) => {
        if (!newFiles || newFiles.length === 0) return;
        const uploadPromises = newFiles.map(file => {
            const fileFormData = new FormData();
            fileFormData.append('GroupId', String(groupId));
            fileFormData.append('Description', 'DEALER_INVOICE');
            fileFormData.append('File', file);
            return uploadDocument(fileFormData).unwrap();
        });
        await Promise.all(uploadPromises);
        setNewFiles([]);
    };

    const uploadFilesSafely = async (groupId) => {
        try {
            await uploadFiles(groupId);
            return true;
        } catch (uploadError) {
            console.error("Upload Error:", uploadError);
            showToast('error', th('common.error'), th('dealerInvoice.messages.errorDetail'));
            return false;
        }
    };

    const switchToDraftEdit = (recordId, groupId) => {
        setSelectedRecord({
            recordId,
            groupId,
            invoiceNumber: formData.invoiceNumber,
            invoiceDate: formData.invoiceDate,
            description: formData.description.trim(),
            totalPrice: formData.totalPrice,
            currency: formData.currency,
            status: InvoiceStatus.DRAFT
        });
        setIsEditMode(true);
        setSubmitted(false);
        loadExistingDocuments(groupId);
    };

    // --- SAVE DRAFT ---
    const handleSaveDraft = async () => {
        setSendAttempted(false);
        if (!validateFields()) return;

        const groupId = formData.groupId;
        setIsSavingDraft(true);

        try {
            if (isDraftEdit) {
                await updateDraft(buildDraftPayload()).unwrap();
                const uploaded = await uploadFilesSafely(groupId);
                if (uploaded) {
                    showToast('success', th('common.success'), th('dealerInvoice.messages.draftUpdated'));
                    hideDialog();
                } else {
                    loadExistingDocuments(groupId);
                }
                refetchList();
                return;
            }

            const newRecordId = await saveDraft({
                GroupId: groupId,
                InvoiceNumber: formData.invoiceNumber,
                InvoiceDate: formData.invoiceDate,
                TotalPrice: formData.totalPrice,
                Description: formData.description.trim()
            }).unwrap();

            const uploaded = await uploadFilesSafely(groupId);
            if (uploaded) {
                showToast('success', th('common.success'), th('dealerInvoice.messages.draftSaved'));
                hideDialog();
            } else {
                switchToDraftEdit(newRecordId, groupId);
            }
            refetchList();
        } catch (error) {
            console.error("Save Draft Error:", error);
            showToast('error', th('common.error'), getErrorDetail(error, 'dealerInvoice.messages.draftSaveError'));
        } finally {
            setIsSavingDraft(false);
        }
    };

    // --- SUBMIT DRAFT ---
    const handleSubmitDraft = async () => {
        if (!validateForSend()) return;

        const groupId = formData.groupId;
        setIsSending(true);

        try {
            await updateDraft(buildDraftPayload()).unwrap();
            await uploadFiles(groupId);
            await submitDraft({ RecordId: formData.id }).unwrap();

            const successMsg = isDomesticDealer
                ? th('warrantyTeam.messages.approveSuccess')
                : th('dealerInvoice.messages.submittedSuccessfully');

            showToast('success', th('common.success'), successMsg);
            hideDialog();
            refetchList();
        } catch (error) {
            console.error("Submit Draft Error:", error);
            loadExistingDocuments(groupId);
            showToast('error', th('common.error'), getErrorDetail(error, 'dealerInvoice.messages.submitError'));
        } finally {
            setIsSending(false);
        }
    };

    // --- SUBMIT ---
    const handleSubmit = async () => {
        if (isDraftEdit) {
            await handleSubmitDraft();
            return;
        }

        if (!validateForSend()) return;

        setIsSending(true);

        try {
            if (isEditMode) {
                // UPDATE (RETURNED)
                const payload = {
                    RecordId: formData.id, // ID form data'dan
                    InvoiceNumber: formData.invoiceNumber,
                    InvoiceDate: formData.invoiceDate,
                    TotalPrice: formData.totalPrice,
                    Description: formData.description.trim()
                };

                await updateInvoice(payload).unwrap();

                // Varsa yeni dosyaları yükle
                await uploadFiles(formData.groupId);
            } else {
                // CREATE
                const payload = {
                    GroupId: formData.groupId,
                    InvoiceNumber: formData.invoiceNumber,
                    InvoiceDate: formData.invoiceDate,
                    TotalPrice: formData.totalPrice,
                    Description: formData.description.trim()
                };
                await createInvoice(payload).unwrap();
                await uploadFiles(formData.groupId);
            }

            const successMsg = isDomesticDealer
                ? th('warrantyTeam.messages.approveSuccess')
                : th('dealerInvoice.messages.submittedSuccessfully');

            showToast('success', th('common.success'), successMsg);
            hideDialog();
            refetchList();
        } catch (error) {
            console.error("Submit Error:", error);
            showToast('error', th('common.error'), getErrorDetail(error, 'dealerInvoice.messages.submitError'));
        } finally {
            setIsSending(false);
        }
    };

    const onDeleteDocument = async (doc) => {
        try {
            const targetGroupId = formData.groupId || selectedRecord?.groupId || selectedRecord?.vbeln;

            await deleteDocument({
                documentId: doc.id,
                groupId: targetGroupId
            }).unwrap();

            setExistingDocuments(prev => prev.filter(d => d.id !== doc.id));
            showToast('success', th('common.success'), th('dealerInvoice.messages.deleted'));
        } catch (error) {
            console.error("Delete Error:", error);
            showToast('error', th('common.error'), th('dealerInvoice.messages.errorDetail'));
        }
    };

    const onDownloadDocument = async (doc) => {
        const key = doc.fileKey || doc.id;
        if (!key) return;
        if (toastRef?.current) {
            toastRef.current.show({ severity: 'info', summary: th('common.downloading'), detail: th('common.downloading'), life: 3000 });
        }
        try {
            await downloadService.downloadByKey(triggerDownloadByKey, key, null, { successMessage: th('common.success'), errorMessage: th('common.error') });
        } catch (error) { console.error("Download Error", error); }
    };

    // Action handlers (Approve, Reject, Return) buraya aynen gelecek...
    const handleApprove = async (rowData, note) => {
        const id = getRecordId(rowData);
        if (!id) return;
        try { await approveInvoice({ RecordId: id, ApprovalNote: note || null }).unwrap(); showToast('success', th('common.success'), th('dealerInvoice.messages.approvedSuccessfully')); refetchList(); }
        catch (error) { showToast('error', th('common.error'), th('dealerInvoice.messages.approveError')); }
    };
    const handleReject = async (rowData, reason) => {
        const id = getRecordId(rowData);
        if (!id) return;
        try { await rejectInvoice({ RecordId: id, RejectionReason: reason }).unwrap(); showToast('success', th('common.success'), th('dealerInvoice.messages.rejectedSuccessfully')); refetchList(); }
        catch (e) { showToast('error', th('common.error'), th('dealerInvoice.messages.rejectError')); }
    };
    const handleReturn = async (rowData, reason) => {
        const id = getRecordId(rowData);
        if (!id) return;
        try { await returnInvoice({ RecordId: id, ReturnReason: reason }).unwrap(); showToast('success', th('common.success'), th('dealerInvoice.messages.returnedSuccessfully')); refetchList(); }
        catch (e) { showToast('error', th('common.error'), th('dealerInvoice.messages.returnError')); }
    };

    return {
        dialogVisible, openNew, openEdit, hideDialog, formData,
        onInputChange, onDateChange, onTextareaChange,
        submitted, sendAttempted,
        handleSubmit, handleSaveDraft, handleSubmitDraft, validateForSend,
        handleApprove, handleReject, handleReturn,
        isSubmitting: isSavingDraft || isSending || isCreating || isUpdating || isUploading,
        isSavingDraft, isSending,
        isApproving, isRejecting, isReturning,
        isEditMode,
        isLoadingDetail,
        newFiles, onFilesChange, existingDocuments, onDeleteDocument, onDownloadDocument, setToastCallback
    };
};
